import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../UI/Button';
import { useSurvey } from '../../context/SurveyContext';

// Product-specific pricing
const productPricing = {
  nad: {
    price: 179,
    originalPrice: 249,
    savings: 70
  },
  glutathione: {
    price: 109,
    originalPrice: 169,
    savings: 60
  },
  vitaminb12: {
    price: 99,
    originalPrice: 129,
    savings: 30
  },
  lcarnitine: {
    price: 99,
    originalPrice: 149,
    savings: 50
  }
};

export default function ProductOfferScreen({ product, checkoutUrl, onContinue }) {
  const pricing = productPricing[product?.id] || productPricing.nad;
  const { answers } = useSurvey();

  // Countdown timer state (starts at 5 minutes = 300 seconds)
  const [timeLeft, setTimeLeft] = useState(300);

  useEffect(() => {
    // Don't count down if already at 0
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Format seconds as M:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Mutate checkout URL: replace /checkout with /checkout-slim and add user params
  const mutateCheckoutUrl = (url) => {
    if (!url) return url;

    try {
      // Replace /checkout with /checkout-slim
      let mutatedUrl = url.replace('/checkout', '/checkout-slim');

      // Parse existing URL to preserve query params
      const urlObj = new URL(mutatedUrl);

      // Add user data parameters from survey answers
      const userParams = {
        firstName: answers.firstName,
        lastName: answers.lastName,
        emailAddress: answers.email,
        phoneNumber: answers.phone,
        shipAddress1: answers.shippingAddress1,
        shipAddress2: answers.shippingAddress2,
        shipCity: answers.shippingCity,
        shipPostalCode: answers.shippingZip,
        shipState: answers.shippingState
      };

      // Add each param to URL if it exists
      Object.entries(userParams).forEach(([key, value]) => {
        if (value) {
          urlObj.searchParams.set(key, value);
        }
      });

      return urlObj.toString();
    } catch (error) {
      console.error('Error mutating checkout URL:', error);
      return url; // Return original URL if mutation fails
    }
  };

  // Handle BUY NOW click - redirect to checkout
  const handleBuyNow = () => {
    if (checkoutUrl) {
      const mutatedUrl = mutateCheckoutUrl(checkoutUrl);
      console.log('Original checkout URL:', checkoutUrl);
      console.log('Mutated checkout URL:', mutatedUrl);
      window.location.href = mutatedUrl;
    } else {
      // Fallback if checkout URL not available
      onContinue();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-2xl mx-auto"
    >
      <style>{`
        @keyframes pulse-dot {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.1);
          }
        }
        .pulse-dot {
          animation: pulse-dot 2s ease-in-out infinite;
        }
      `}</style>

      {/* Urgency Banner */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6 text-center">
        <p className="text-slate text-sm mb-3">
          Your approval is reserved for <span className="text-renuvia-gold font-bold">{formatTime(timeLeft)}</span>
        </p>

        <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-3">
          Save ${pricing.savings} <span className="text-renuvia-gold italic font-normal">Instantly</span>
        </h2>

        <p className="text-slate text-sm mb-2">
          No contracts, cancel anytime without speaking to anyone.
        </p>

        <p className="text-slate text-sm font-medium">
          24/7 Support • unlimited doctor visits • medication all included.
        </p>
      </div>

      <div className="bg-white rounded-luxury shadow-luxury p-8">
        {/* IN STOCK Badge */}
        <div className="flex items-center gap-2 mb-6">
          <span className="w-2.5 h-2.5 bg-onyx-green rounded-full pulse-dot"></span>
          <span className="bg-onyx-green text-white text-xs font-bold px-3 py-1 rounded-full">
            IN STOCK
          </span>
        </div>

        {/* Product Title & Concentration */}
        <div className="text-center mb-6">
          <h1 className="text-2xl md:text-3xl font-black text-charcoal mb-1">
            RENUVIA {product.name}
          </h1>
          <p className="text-lg text-slate">
            {product.ingredients[0].amount}
          </p>
        </div>

        {/* Product Image */}
        <div
          onClick={handleBuyNow}
          className="relative w-full aspect-square max-w-[280px] mx-auto bg-gradient-to-br from-warm-beige to-gray-50 rounded-xl mb-4 flex items-center justify-center overflow-hidden cursor-pointer transition-transform hover:scale-105"
        >
          <img
            src={`/images/products/${product.id}.webp`}
            alt={`${product.name} product`}
            className="w-full h-full object-contain p-6"
          />
        </div>

        {/* Reviews Summary */}
        <div className="flex flex-col items-center gap-2 mb-6">
          <div className="flex gap-1">
            {[...Array(5)].map((_, i) => (
              <svg key={i} className="w-5 h-5 text-renuvia-gold" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
          <span className="text-charcoal font-semibold text-sm">2.1K REVIEWS</span>
        </div>

        {/* Tagline */}
        <p className="text-xl text-renuvia-gold text-center mb-4">
          {product.tagline}
        </p>

        {/* Product Description */}
        <p className="text-lg text-center text-slate mb-6">
          {product.description}
        </p>

        {/* Pricing */}
        <div className="border-2 border-renuvia-gold/30 rounded-xl p-6 mb-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <p className="text-sm text-slate mb-1">Prescribed for only</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-renuvia-gold">${pricing.price}.00</span>
                <span className="text-slate">/ first {product.priceInterval}</span>
              </div>
            </div>
            <div className="text-right">
              <span className="line-through text-slate text-xl">${pricing.originalPrice}</span>
              <p className="text-sm font-bold text-onyx-green">Save ${pricing.savings}</p>
            </div>
          </div>

          {/* What's Included */}
          <div className="border-t border-gray-200 pt-4">
            <p className="font-semibold text-charcoal mb-3">PRICE INCLUDES:</p>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-renuvia-gold flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-charcoal text-sm">Premium {product.name} for anti-aging therapy</span>
              </div>
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-renuvia-gold flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-charcoal text-sm">Doctor consult, unlimited 1:1 medical support, written prescription + 4 weeks of medicine</span>
              </div>
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-renuvia-gold flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <div className="flex items-center gap-2">
                  <span className="text-charcoal text-sm font-semibold">FREE overnight shipping</span>
                  <span className="bg-renuvia-gold text-white text-xs font-bold px-2 py-0.5 rounded">FREE</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Benefits */}
        <div className="mb-6">
          <div className="space-y-3">
            {product.benefits.slice(0, 3).map((benefit, i) => (
              <div key={i} className="flex items-center gap-3">
                <svg className="w-6 h-6 text-renuvia-gold flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-charcoal">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* BUY NOW Button */}
        <Button onClick={handleBuyNow} variant="green" fullWidth className="mb-6 text-lg py-4">
          BUY NOW
        </Button>

        {/* Trust Badges */}
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-onyx-green flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-charcoal text-sm font-medium">Most popular {product.name} therapy program</span>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-onyx-green flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-charcoal text-sm font-medium">Prescribed by world-class, US based, board-certified clinicians</span>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-onyx-green flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-charcoal text-sm font-medium">Free priority overnight shipping</span>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-onyx-green flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-charcoal text-sm font-medium">Send a message to your doctor 24/7</span>
          </div>
        </div>

        {/* Risk-Free Guarantee */}
        <div className="mt-8 bg-gradient-to-r from-onyx-green/10 to-renuvia-gold/10 rounded-xl p-6">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <svg className="w-6 h-6 text-onyx-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <p className="font-bold text-charcoal text-lg">Risk-Free Promise</p>
            </div>
            <div className="space-y-2">
              <p className="text-charcoal font-semibold">✓ Only get charged once one of our doctors approves your prescription</p>
              <p className="text-charcoal font-semibold">✓ Cancel anytime, no commitment</p>
              <p className="text-charcoal font-semibold">✓ Medical provider oversight</p>
            </div>
          </div>
        </div>

        {/* Medical Disclaimer */}
        <div className="mt-6">
          <p className="text-xs text-center text-slate">
            Your intake will be reviewed by a licensed medical provider who will determine if this treatment is appropriate for you.
          </p>
        </div>
      </div>
    </motion.div>
  );
}
